import React from "react";
import { MessageSquareText, ShieldCheck, Star } from "lucide-react";
import { useLang } from "../lib/i18n";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";

const YANDEX_BADGE_SRC = "https://yandex.ru/sprav/widget/rating-badge/149759078052?type=rating";
const TWOGIS_WIDGET_SRC = "https://widget.2gis.ru/api/widget?org_id=70000001117158051&branch_id=70000001117158052&size=medium&theme=light";

export default function Reviews() {
  const { lang } = useLang();

  return (
    <div className="min-h-screen bg-[#F3F0E8] text-[#342116]" data-testid="reviews-page">
      <Seo
        path="/reviews"
        lang={lang}
        title={lang === "ru" ? "Отзывы об АРГАЛИ — Яндекс" : "АРГАЛИ reviews — Yandex"}
        description={lang === "ru"
          ? "Отзывы и рейтинг АРГАЛИ на независимых площадках. Основной источник отзывов — Яндекс."
          : "АРГАЛИ reviews and ratings from independent platforms. Yandex is our primary review source."}
      />

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-32 pb-16">
        <Reveal>
          <p className="eyebrow">{lang === "ru" ? "Отзывы путешественников" : "Guest reviews"}</p>
          <h1 className="display display-md mt-3 max-w-5xl">
            {lang === "ru" ? "Отзывы об АРГАЛИ" : "Reviews of АРГАЛИ"}
          </h1>
          <p className="max-w-3xl mt-6 text-[#5f4937] leading-relaxed text-lg">
            {lang === "ru"
              ? "Мы собираем отзывы на независимых площадках, чтобы оценка и история отзывов оставались прозрачными. Для российских гостей основная площадка — Яндекс."
              : "We collect reviews on independent platforms so ratings and review history stay transparent. Yandex is the primary platform for our Russian guests."}
          </p>
        </Reveal>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24">
        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] border border-[#342116]/15 bg-white/25">
            <div className="p-7 md:p-10 lg:p-12 border-b lg:border-b-0 lg:border-r border-[#342116]/15">
              <div className="flex items-center gap-3 text-[#A96818]">
                <Star size={19} />
                <span className="text-xs uppercase tracking-[0.18em]">Yandex Business</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl mt-5">
                {lang === "ru" ? "Рейтинг и отзывы в Яндексе" : "Yandex rating and reviews"}
              </h2>
              <p className="mt-4 text-[#5f4937] leading-relaxed max-w-xl">
                {lang === "ru"
                  ? "Бейдж ниже загружается напрямую из Яндекса, поэтому рейтинг обновляется там же, где гости оставляют отзывы. Нажмите на него, чтобы открыть карточку АРГАЛИ в Яндексе."
                  : "The badge below is loaded directly from Yandex, so the rating is updated at the same source where guests leave their reviews. Click it to open АРГАЛИ on Yandex."}
              </p>

              <div className="mt-8 inline-flex items-center justify-center min-w-[190px] min-h-[80px] px-5 py-4 bg-white border border-[#342116]/10 shadow-sm">
                <iframe
                  src={YANDEX_BADGE_SRC}
                  width="150"
                  height="50"
                  frameBorder="0"
                  title={lang === "ru" ? "Рейтинг АРГАЛИ в Яндексе" : "АРГАЛИ rating on Yandex"}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              <p className="mt-5 text-xs text-[#7d6958] max-w-lg">
                {lang === "ru"
                  ? "Источник: Яндекс. АРГАЛИ не редактирует и не переносит отзывы в Яндексе."
                  : "Source: Yandex. АРГАЛИ does not edit or recreate reviews submitted on Yandex."}
              </p>
            </div>

            <div className="p-7 md:p-10 lg:p-12 flex flex-col justify-center">
              <div className="space-y-7">
                <div className="flex gap-4">
                  <ShieldCheck className="text-[#A96818] shrink-0 mt-0.5" size={22} />
                  <div>
                    <h3 className="font-serif text-xl">{lang === "ru" ? "Независимый источник" : "Independent source"}</h3>
                    <p className="text-sm text-[#6f5947] mt-1 leading-relaxed">{lang === "ru" ? "Оценки публикуются на стороне Яндекса, а не создаются администраторами сайта." : "Ratings are published by Yandex, not created by the website administrators."}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <MessageSquareText className="text-[#A96818] shrink-0 mt-0.5" size={22} />
                  <div>
                    <h3 className="font-serif text-xl">{lang === "ru" ? "Оставить отзыв" : "Leave a review"}</h3>
                    <p className="text-sm text-[#6f5947] mt-1 leading-relaxed">{lang === "ru" ? "Нажмите на официальный бейдж Яндекса и оставьте отзыв в карточке компании." : "Click the official Yandex badge and leave your review on the business profile."}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24">
        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] border border-[#342116]/15 bg-white/25">
            <div className="p-7 md:p-10 lg:p-12 border-b lg:border-b-0 lg:border-r border-[#342116]/15">
              <div className="flex items-center gap-3 text-[#A96818]">
                <Star size={19} />
                <span className="text-xs uppercase tracking-[0.18em]">2ГИС</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl mt-5">
                {lang === "ru" ? "Рейтинг и отзывы в 2ГИС" : "2GIS rating and reviews"}
              </h2>
              <p className="mt-4 text-[#5f4937] leading-relaxed max-w-xl">
                {lang === "ru"
                  ? "Виджет ниже загружается напрямую из 2ГИС, поэтому рейтинг обновляется там же, где гости оставляют отзывы. Нажмите на него, чтобы открыть карточку АРГАЛИ в 2ГИС."
                  : "The widget below is loaded directly from 2GIS, so the rating is updated at the same source where guests leave their reviews. Click it to open АРГАЛИ on 2GIS."}
              </p>

              <div className="mt-8 inline-flex items-center justify-center min-w-[190px] min-h-[80px] px-5 py-4 bg-white border border-[#342116]/10 shadow-sm">
                <iframe
                  src={TWOGIS_WIDGET_SRC}
                  width="150"
                  height="50"
                  frameBorder="0"
                  title={lang === "ru" ? "Рейтинг АРГАЛИ в 2ГИС" : "АРГАЛИ rating on 2GIS"}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              <p className="mt-5 text-xs text-[#7d6958] max-w-lg">
                {lang === "ru"
                  ? "Источник: 2ГИС. АРГАЛИ не редактирует и не переносит отзывы в 2ГИС."
                  : "Source: 2GIS. АРГАЛИ does not edit or recreate reviews submitted on 2GIS."}
              </p>
            </div>

            <div className="p-7 md:p-10 lg:p-12 flex flex-col justify-center">
              <div className="space-y-7">
                <div className="flex gap-4">
                  <ShieldCheck className="text-[#A96818] shrink-0 mt-0.5" size={22} />
                  <div>
                    <h3 className="font-serif text-xl">{lang === "ru" ? "Независимый источник" : "Independent source"}</h3>
                    <p className="text-sm text-[#6f5947] mt-1 leading-relaxed">{lang === "ru" ? "Оценки публикуются на стороне 2ГИС, а не создаются администраторами сайта." : "Ratings are published by 2GIS, not created by the website administrators."}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <MessageSquareText className="text-[#A96818] shrink-0 mt-0.5" size={22} />
                  <div>
                    <h3 className="font-serif text-xl">{lang === "ru" ? "Оставить отзыв" : "Leave a review"}</h3>
                    <p className="text-sm text-[#6f5947] mt-1 leading-relaxed">{lang === "ru" ? "Нажмите на официальный виджет 2ГИС и оставьте отзыв в карточке компании." : "Click the official 2GIS widget and leave your review on the business profile."}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}